import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  InputBase,
  SxProps,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  ListboxToolbarContext,
  ListboxToolbarValue,
  ToolbarPaper,
} from './AddFilterListValueToolbar';

interface LabelValueOption {
  label: string;
  value: string;
  /** Stable unique key for React reconciliation. When two options share the
   *  same label (e.g. two contracts with the same name under different clients),
   *  supply this to avoid duplicate React keys and the ghost-node DOM
   *  accumulation that results. Falls back to `value` when absent. */
  optionKey?: string;
  /** Alternative string used for filter matching. When provided, the filter
   *  compares the user's input against this field instead of `label`. Lets
   *  options with long display labels (e.g. "CLIENT - Contract Name") be
   *  searched by a shorter or differently-structured string while the full
   *  label is shown in the dropdown. */
  searchText?: string;
}

// Module-level filter function — defined outside the component so it has a
// stable reference and never triggers Autocomplete re-renders due to a
// changed prop. Optimised for options that carry a pre-lowercased
// `searchText` (e.g. contract options with "clientCode contractName"):
// those options need no per-keystroke string allocation at all. For options
// without `searchText` (Status, Service Type, etc.) the function falls back
// to a single `label.toLowerCase()` call — the same cost as the previous
// implementation. In both cases the expensive normalize/diacritic-strip
// that MUI's createFilterOptions runs by default is skipped entirely.
const filterOptions = (
  options: LabelValueOption[],
  { inputValue }: { inputValue: string }
): LabelValueOption[] => {
  const needle = inputValue.toLowerCase().trim();
  if (!needle) return options;
  return options.filter((opt) => {
    const haystack = opt.searchText !== undefined ? opt.searchText : opt.label.toLowerCase();
    return haystack.includes(needle);
  });
};

export interface AddFilterListValuePickerProps {
  fieldName: string;
  values: string[];
  onChange: (next: string[]) => void;
  /** When provided, render an Autocomplete multiselect; otherwise fall back
   *  to a comma-separated InputBase (used by list-type fields that don't yet
   *  have a BFF source, e.g. Brand / Priority / Survey Status). */
  options?: LabelValueOption[];
  /** Styling for the fallback InputBase only; ignored when `options` are
   *  supplied (the Autocomplete provides its own input styling). */
  inputSx?: SxProps<Theme>;
}

export const AddFilterListValuePicker: React.FC<AddFilterListValuePickerProps> = ({
  fieldName,
  values,
  onChange,
  options,
  inputSx,
}) => {
  const theme = useTheme();

  // Single source of truth for "what's actually selected" — derived from
  // the option set so stale values carried over from a saved report
  // (e.g. a structure type that's since been deactivated) don't show up
  // anywhere. Drives the chips/ticks, the toolbar count, and the
  // placeholder visibility consistently.
  const selectedOptions = useMemo(
    () => (options ? options.filter((opt) => values.includes(opt.value)) : []),
    [options, values]
  );

  const toolbarValue = useMemo<ListboxToolbarValue | null>(() => {
    if (!options) return null;
    return {
      selectedCount: selectedOptions.length,
      totalCount: options.length,
      onSelectAll: () => onChange(options.map((opt) => opt.value)),
      onClear: () => onChange([]),
      accentColor: theme.palette.v4.accent,
    };
  }, [options, selectedOptions.length, onChange, theme.palette.v4.accent]);

  if (options) {
    return (
      <ListboxToolbarContext.Provider value={toolbarValue}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          size="small"
          options={options}
          getOptionLabel={(opt) => opt.label}
          getOptionKey={(opt) => opt.optionKey ?? opt.value}
          filterOptions={filterOptions}
          isOptionEqualToValue={(opt, val) => opt.value === val.value}
          value={selectedOptions}
          onChange={(_, selected) => onChange(selected.map((opt) => opt.value))}
          PaperComponent={ToolbarPaper}
          renderOption={(props, option, { selected }) => {
            const { key, ...liProps } = props as React.HTMLAttributes<HTMLLIElement> & {
              key?: React.Key;
            };
            return (
              <Box
                component="li"
                {...liProps}
                key={key}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Checkbox checked={selected} size="small" tabIndex={-1} disableRipple />
                <Typography variant="body2">{option.label}</Typography>
              </Box>
            );
          }}
          renderTags={(selected, getTagProps) =>
            selected.map((opt, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={key}
                  size="small"
                  label={opt.label}
                  sx={{
                    bgcolor: theme.palette.v4.accent,
                    color: theme.palette.v4.surface,
                    '& .MuiChip-deleteIcon': { color: theme.palette.v4.surface },
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                selectedOptions.length === 0 ? `Select ${fieldName.toLowerCase()}...` : ''
              }
              inputProps={{
                ...params.inputProps,
                'aria-label': `${fieldName} values`,
              }}
            />
          )}
          slotProps={{
            listbox: {
              sx: { maxHeight: '200px' },
            },
          }}
          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
        />
      </ListboxToolbarContext.Provider>
    );
  }

  return (
    <CsvFallback
      fieldName={fieldName}
      values={values}
      onChange={onChange}
      inputSx={inputSx}
      theme={theme}
    />
  );
};

interface CsvFallbackProps extends AddFilterListValuePickerProps {
  theme: Theme;
}

// Displays and edits a comma-separated list of raw strings while preserving
// what the user is typing (including a trailing `,` and an empty token). We
// keep the raw text in local state so typing a comma doesn't round-trip
// through a filtered `values` array and snap back, and only publish the
// trimmed/non-empty tokens upstream.
const CsvFallback: React.FC<CsvFallbackProps> = ({
  fieldName,
  values,
  onChange,
  inputSx,
  theme,
}) => {
  const [text, setText] = useState(() => values.join(', '));
  const lastPublishedRef = useRef<string>(values.join(','));

  // Keep local text in sync when the parent resets `values` externally
  // (e.g. on back/reset) without overwriting mid-typing state.
  useEffect(() => {
    const parentKey = values.join(',');
    if (parentKey !== lastPublishedRef.current) {
      setText(values.join(', '));
      lastPublishedRef.current = parentKey;
    }
  }, [values]);

  return (
    <>
      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 1 }}>
        Enter values separated by commas, or paste a column from a spreadsheet
      </Typography>
      <InputBase
        // `multiline` so a column pasted from a spreadsheet keeps its line
        // breaks — a single-line <input> silently strips `\n`, which would
        // merge the pasted values into one token.
        multiline
        maxRows={6}
        placeholder="Value1, Value2..."
        value={text}
        onChange={(e) => {
          const raw = e.target.value;
          // Split on commas, semicolons, and newlines so a block pasted
          // straight from a spreadsheet column lands as individual values.
          const next = raw
            .split(/[,;\r\n]+/)
            .map((v) => v.trim())
            .filter(Boolean);
          // Preserve the raw text while the user is typing a single line (so a
          // trailing comma isn't stripped mid-entry); collapse multi-line or
          // semicolon-delimited pastes to a tidy comma list.
          setText(/[;\r\n]/.test(raw) ? next.join(', ') : raw);
          const nextKey = next.join(',');
          if (nextKey !== lastPublishedRef.current) {
            lastPublishedRef.current = nextKey;
            onChange(next);
          }
        }}
        fullWidth
        inputProps={{ 'aria-label': `${fieldName} values` }}
        sx={inputSx}
      />
    </>
  );
};
